import { calculateAgeAtDate } from '../dateUtils/dateUtils';

export const AGE_RULES = {
  principal: { min: 18, max: 79 },
  spouse: { min: 18, max: 79 },
  child: { min: 0, max: 25 },
};

export const QUOTE_EXPIRY_DAYS = 30;
export const START_DATE_DELAY_DAYS = 1;

export const RETRIEVAL_OUTCOMES = {
  UNCHANGED: 'unchanged',
  PREMIUM_CHANGED: 'premium-changed',
  DEPENDANT_INVALID: 'dependant-invalid',
  PRINCIPAL_INVALID: 'principal-invalid',
  PREVENT_SALE: 'prevent-sale',
};

const isWithinRule = (age, rule) =>
  age !== null && !Number.isNaN(age) && age >= rule.min && age <= rule.max;

const toIsoDateOnly = date => date.toISOString().split('T')[0];

const resolveCoverDates = (expiredDate, originalCoverDate, today) => {
  const dateInPast = originalCoverDate <= today;

  let quoteExpired = false;
  if (expiredDate) {
    quoteExpired = expiredDate < today;
  } else if (dateInPast) {
    const diffMs = today.getTime() - originalCoverDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    quoteExpired = diffDays > QUOTE_EXPIRY_DAYS;
  }

  const needsCoverDateUpdate = dateInPast || quoteExpired;

  let effectiveCoverDate = originalCoverDate;
  if (needsCoverDateUpdate) {
    effectiveCoverDate = new Date(today);
    effectiveCoverDate.setDate(effectiveCoverDate.getDate() + START_DATE_DELAY_DAYS);
  }

  return {
    dateInPast,
    quoteExpired,
    needsCoverDateUpdate,
    originalCoverDate,
    effectiveCoverDate,
  };
};

const collectInvalidDependants = (dependents, originalCoverDate, effectiveCoverDate) => {
  const invalidDependants = [];
  let dependantAgesChanged = false;

  const spouse = dependents?.spouse;
  if (spouse?.dateOfBirth) {
    dependantAgesChanged = checkDependantValidity(
      spouse.dateOfBirth,
      originalCoverDate,
      effectiveCoverDate,
      dependantAgesChanged,
      invalidDependants,
      AGE_RULES.spouse,
      { type: 'spouse' }
    );
  }

  for (const [index, child] of (dependents?.children || []).entries()) {
    if (!child?.dateOfBirth) {
      continue;
    }

    dependantAgesChanged = checkDependantValidity(
      child.dateOfBirth,
      originalCoverDate,
      effectiveCoverDate,
      dependantAgesChanged,
      invalidDependants,
      AGE_RULES.child,
      { type: 'child', index }
    );
  }

  return { invalidDependants, dependantAgesChanged };
};

const checkDependantValidity = (
  dateOfBirth,
  originalCoverDate,
  effectiveCoverDate,
  dependantAgesChanged,
  invalidDependants,
  ageRules,
  dependantInfo
) => {
  const previousAge = calculateAgeAtDate(dateOfBirth, originalCoverDate);
  const age = calculateAgeAtDate(dateOfBirth, effectiveCoverDate);
  if (previousAge !== age) {
    dependantAgesChanged = true;
  }
  if (!isWithinRule(age, ageRules)) {
    invalidDependants.push(dependantInfo);
  }
  return dependantAgesChanged;
};

const validateRetrievedQuote = (data, now = new Date()) => {
  const today = new Date(toIsoDateOnly(now));
  const prospect = data.prospectDetails;
  const quoteDetails = data.quoteDetails[0];
  const originalCoverDate = new Date(quoteDetails.coverStartDate);
  const expiredDate = quoteDetails.expiredDate ? new Date(quoteDetails.expiredDate) : null;
  const { dateInPast, quoteExpired, needsCoverDateUpdate, effectiveCoverDate } = resolveCoverDates(
    expiredDate,
    originalCoverDate,
    today
  );
  const principalAgeBefore = calculateAgeAtDate(prospect.dateOfBirth, originalCoverDate);
  const principalAgeAtEffective = calculateAgeAtDate(prospect.dateOfBirth, effectiveCoverDate);
  const principleAgeChanged = principalAgeBefore !== principalAgeAtEffective;
  const principalAgeNow = calculateAgeAtDate(prospect.dateOfBirth, today);
  if (
    !isWithinRule(principalAgeNow, AGE_RULES.principal) ||
    !isWithinRule(principalAgeAtEffective, AGE_RULES.principal)
  ) {
    return { outcome: RETRIEVAL_OUTCOMES.PRINCIPAL_INVALID, data };
  }

  const { invalidDependants: invalidDependants, dependantAgesChanged } = collectInvalidDependants(
    prospect.dependents,
    originalCoverDate,
    effectiveCoverDate
  );

  let updatedData = data;

  if (needsCoverDateUpdate) {
    const isoCoverDate = toIsoDateOnly(effectiveCoverDate);
    updatedData = {
      ...data,
      quoteDetails: data.quoteDetails.map(quote => ({
        ...quote,
        coverStartDate: isoCoverDate,
      })),
    };
  }

  if (invalidDependants.length) {
    return {
      outcome: RETRIEVAL_OUTCOMES.DEPENDANT_INVALID,
      data: updatedData,
      invalidDependants,
    };
  }
  if (dateInPast || quoteExpired || principleAgeChanged || dependantAgesChanged) {
    return { outcome: RETRIEVAL_OUTCOMES.PREMIUM_CHANGED, data: updatedData };
  }

  return { outcome: RETRIEVAL_OUTCOMES.UNCHANGED, data: updatedData };
};

export default validateRetrievedQuote;
